import React, { Children, forwardRef, useCallback, useEffect, useMemo, useState, } from "react";
import { getClassName } from "@sscs/utils";
import { Table } from "../../";
import { useDataTableColumnCount, useDataTableFlush } from "../context";
import ExpandedRowContext from "./context";
import styles from "./expandedRow.module.scss";
/**
 * Two-part table row: an always-visible summary row plus a collapsible details region.
 * MUST render inside a `DataTable` — the details cell spans the visible columns read from context.
 */
const ExpandedRow = forwardRef(({ disabled = false, focused = false, id, expanded, className, emphasizeExpanded = true, onChange, onClick, children, ...props }, ref) => {
    const [summary, ...details] = Children.toArray(children);
    const [expandedState, setExpandedState] = useState(expanded === undefined ? false : expanded);
    const isFocused = expandedState && focused;
    const columnCount = useDataTableColumnCount();
    const flush = useDataTableFlush();
    if (columnCount === undefined && process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("DataTable.ExpandedRow must be rendered inside a DataTable so its details cell can span the table's columns.");
    }
    useEffect(() => {
        if (expanded !== undefined)
            setExpandedState(expanded);
    }, [expanded]);
    const handleChange = useCallback(() => {
        setExpandedState(!expandedState);
        if (onChange) {
            onChange(!expandedState);
        }
    }, [expandedState, onChange, setExpandedState]);
    const contextValue = useMemo(() => ({
        id,
        expanded: expandedState,
        disabled,
        handleChange,
    }), [id, expandedState, disabled, handleChange]);
    return (React.createElement(ExpandedRowContext.Provider, { value: contextValue },
        React.createElement(Table.Row, { ...props, ref: ref, hover: expandedState ? false : true, className: getClassName(styles.summary, {
                [styles["expanded-summary"]]: expandedState && emphasizeExpanded,
                [styles["focus-summary"]]: isFocused,
            }, className), onClick: () => (onClick ? onClick() : null) }, summary),
        React.createElement(Table.Row, { className: getClassName(styles.details, {
                [styles["focus-details"]]: isFocused,
                [styles["focus-details-flush"]]: isFocused && flush,
            }), onClick: () => (onClick ? onClick() : null) },
            React.createElement(Table.Cell, { colSpan: columnCount !== null && columnCount !== void 0 ? columnCount : 1 }, details))));
});
ExpandedRow.displayName = "DataTable.ExpandedRow";
export default ExpandedRow;
//# sourceMappingURL=ExpandedRow.js.map